import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet as WalletIcon, Clock, CheckCircle, XCircle, Check, X, Search, IndianRupee, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminWalletTopups = () => {
  const [data, setData] = useState({ requests: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [rejecting, setRejecting] = useState(null); // {req, reason}
  const [actingId, setActingId] = useState(null);

  const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/admin/topup-requests${qs}`, h);
      setData(res.data);
    } catch {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approve = async (req) => {
    if (!window.confirm(`Approve ₹${req.amount} top-up for ${req.user_name || req.user_phone}?\nThis will credit ${req.credits_to_add} credits to their wallet.`)) return;
    setActingId(req.id);
    try {
      await axios.post(`${API}/admin/topup-requests/${req.id}/approve`, {}, h);
      toast({ title: 'Approved', description: `₹${req.amount} credited to wallet.` });
      fetchData();
    } catch (e) {
      toast({ title: 'Approve failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setActingId(null); }
  };

  const reject = async () => {
    if (!rejecting) return;
    setActingId(rejecting.req.id);
    try {
      const reason = rejecting.reason || 'Payment could not be verified';
      await axios.post(`${API}/admin/topup-requests/${rejecting.req.id}/reject?reason=${encodeURIComponent(reason)}`, {}, h);
      toast({ title: 'Rejected', description: 'Client will see the rejection reason.' });
      setRejecting(null);
      fetchData();
    } catch (e) {
      toast({ title: 'Reject failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setActingId(null); }
  };

  const copyUTR = (utr) => {
    navigator.clipboard.writeText(utr);
    toast({ title: 'UTR copied', description: utr });
  };

  const filtered = (data.requests || []).filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.user_name || '').toLowerCase().includes(s)
      || (r.user_phone || '').includes(s)
      || (r.reference || '').toLowerCase().includes(s);
  });

  const counts = data.counts || {};

  return (
    <div className="p-6" data-testid="admin-wallet-topups">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <WalletIcon className="text-blue-600" /> Wallet Top-Up Requests
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Verify the UTR against your UPI/bank statement, then approve to credit the client's wallet.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-4 border-l-4 border-l-amber-500">
          <p className="text-xs text-gray-500 uppercase">Pending Review</p>
          <p className="text-3xl font-bold text-amber-700 mt-1">{counts.pending || 0}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-green-500">
          <p className="text-xs text-gray-500 uppercase">Approved</p>
          <p className="text-3xl font-bold text-green-700 mt-1">{counts.approved || 0}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-red-500">
          <p className="text-xs text-gray-500 uppercase">Rejected</p>
          <p className="text-3xl font-bold text-red-700 mt-1">{counts.rejected || 0}</p>
        </Card>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="topup-search"
            placeholder="Search name, phone, UTR..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {['pending', 'approved', 'rejected', ''].map(s => (
            <button
              key={s || 'all'}
              data-testid={`filter-${s || 'all'}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition capitalize ${
                statusFilter === s ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <WalletIcon size={40} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-600">
            {statusFilter === 'pending' ? 'No pending top-up requests. You\'re all caught up.' : `No ${statusFilter || 'matching'} requests.`}
          </p>
        </Card>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Submitted</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Method</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">UTR / Ref</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Note</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`topup-row-${r.id}`}>
                  <td className="p-3 text-xs text-gray-600 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3 text-sm">
                    <p className="font-semibold text-gray-900">{r.user_name || '—'}</p>
                    <p className="text-xs text-gray-500">{r.user_phone}</p>
                  </td>
                  <td className="p-3 text-right text-sm font-bold tabular-nums">
                    <span className="inline-flex items-center">
                      <IndianRupee size={12} />{r.amount.toLocaleString()}
                    </span>
                    <p className="text-xs text-gray-400">= {r.credits_to_add} credits</p>
                  </td>
                  <td className="p-3 text-sm capitalize">{(r.method || '').replace('_', ' ')}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <code className="text-xs font-mono text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded">{r.reference}</code>
                      <button onClick={() => copyUTR(r.reference)} className="text-gray-400 hover:text-blue-600 p-0.5" title="Copy UTR"><Copy size={12} /></button>
                    </div>
                  </td>
                  <td className="p-3 text-xs text-gray-600 max-w-[180px] truncate">{r.note || '—'}</td>
                  <td className="p-3">
                    <StatusPill status={r.status} reason={r.reject_reason} />
                  </td>
                  <td className="p-3">
                    {r.status === 'pending' ? (
                      <div className="flex gap-1 justify-end">
                        <Button
                          data-testid={`approve-${r.id}`}
                          size="sm"
                          disabled={actingId === r.id}
                          onClick={() => approve(r)}
                          className="bg-green-600 hover:bg-green-700 h-8"
                          title="Approve and credit"
                        >
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                        <Button
                          data-testid={`reject-${r.id}`}
                          size="sm"
                          variant="outline"
                          disabled={actingId === r.id}
                          onClick={() => setRejecting({ req: r, reason: '' })}
                          className="text-red-700 border-red-300 hover:bg-red-50 h-8"
                          title="Reject"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {r.status === 'approved' ? `Approved ${r.approved_at ? new Date(r.approved_at).toLocaleDateString() : ''}` : `Rejected ${r.rejected_at ? new Date(r.rejected_at).toLocaleDateString() : ''}`}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject reason modal */}
      {rejecting && (
        <Dialog open={true} onOpenChange={() => setRejecting(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-700">Reject Top-Up</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Rejecting <span className="font-semibold">₹{rejecting.req.amount}</span> from <span className="font-semibold">{rejecting.req.user_name}</span>. The client will see your reason.
              </p>
              <div>
                <Label>Reason</Label>
                <textarea
                  data-testid="reject-reason"
                  value={rejecting.reason}
                  onChange={e => setRejecting({ ...rejecting, reason: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., UTR not found in our statement. Please resubmit with the correct UTR."
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setRejecting(null)} className="flex-1">Cancel</Button>
                <Button data-testid="confirm-reject-btn" onClick={reject} className="flex-1 bg-red-600 hover:bg-red-700">Reject Top-Up</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const StatusPill = ({ status, reason }) => {
  const cfg = {
    pending:  { cls: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Pending' },
    approved: { cls: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
    rejected: { cls: 'bg-red-100 text-red-700',     icon: XCircle, label: 'Rejected' },
  }[status] || { cls: 'bg-gray-100 text-gray-700', icon: Clock, label: status };
  const Icon = cfg.icon;
  return (
    <div>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
        <Icon size={11} /> {cfg.label}
      </span>
      {reason && <p className="text-[11px] text-red-700 mt-1 max-w-[180px] truncate" title={reason}>{reason}</p>}
    </div>
  );
};

export default AdminWalletTopups;
